import { useState } from 'react';
import type { Address, Hash } from 'viem';
import { useWalletClient } from 'wagmi';

import type { TransactionQueueItem } from 'components';
import MAX_UINT256 from 'constants/maxUint256';
import {
  getContractAddress,
  isolatedPoolComptrollerAbi,
  positionRouterAbi,
  vBep20Abi,
} from 'libs/contracts';
import { wAlphaAbi, wsTAOAbi } from 'libs/contracts/abis/bittensorWrappers';
import { wrapPositionRouterError } from 'libs/errors';
import { useAccountAddress, useChainId, usePublicClient } from 'libs/wallet';
import type { VToken } from 'types';

import { getVTokenWrapperKind } from '../shared/getVTokenWrapperKind';
import {
  failedStepStateReducer,
  getExchangeRateStored,
  getIsRouterDelegated,
  getRouterQueueStep,
  runRouterQueueStep,
} from '../shared/routerQueueCore';
import { invalidateRouterQueries } from '../shared/routerQueueInvalidation';
import {
  ONE_TOKEN,
  calculateSlippageFloor,
  computeNativeOutFloor,
  computeRouterDeadline,
} from '../shared/routerSlippage';

export interface RouterWithdrawQueuePlanStep extends TransactionQueueItem {
  shouldSkip: boolean;
}

interface ExecuteRouterWithdrawQueueInput {
  underlyingAmountMantissa: bigint;
  withdrawFullSupply: boolean;
  receiveNative: boolean;
  slippagePercentage: number;
  vToken: VToken;
}

export const useRouterWithdrawQueue = () => {
  const { accountAddress } = useAccountAddress();
  const { chainId } = useChainId();
  const { publicClient } = usePublicClient();
  const { data: walletClient } = useWalletClient({ account: accountAddress });
  const [items, setItems] = useState<TransactionQueueItem[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const updateItem = (id: string, status: TransactionQueueItem['status']) => {
    setItems((currentItems) =>
      currentItems.map((item) => (item.id === id ? { ...item, status } : item)),
    );
  };

  const runStep = ({
    action,
    ...step
  }: RouterWithdrawQueuePlanStep & { action: () => Promise<Hash> }) =>
    runRouterQueueStep({ step, action, updateItem, publicClient });

  const execute = async ({
    underlyingAmountMantissa,
    withdrawFullSupply,
    receiveNative,
    slippagePercentage,
    vToken,
  }: ExecuteRouterWithdrawQueueInput) => {
    if (!accountAddress || !walletClient) {
      throw new Error('Wallet is not connected');
    }

    const routerAddress = getContractAddress({ name: 'PositionRouter', chainId });
    const poolComptrollerAddress = getContractAddress({
      name: 'LegacyPoolComptroller',
      chainId,
    });

    if (!routerAddress || !poolComptrollerAddress) {
      throw new Error('PositionRouter or LegacyPoolComptroller address not configured');
    }

    setItems([]);
    setIsRunning(true);

    try {
      const symbol = vToken.underlyingToken.symbol;
      const wrapperKind = await getVTokenWrapperKind({
        publicClient,
        underlyingAddress: vToken.underlyingToken.address,
      });
      if (receiveNative && wrapperKind === 'unsupported') {
        throw new Error(
          `Router withdrawal with receive-as-native is not supported for ${symbol}: underlying is neither a wsTAO nor a wAlpha wrapper.`,
        );
      }
      const isWsTAOMarket = wrapperKind === 'wsTAO';

      const isDelegated = await getIsRouterDelegated({
        accountAddress,
        publicClient,
        poolComptrollerAddress: poolComptrollerAddress as Address,
        routerAddress: routerAddress as Address,
      });

      const plan: RouterWithdrawQueuePlanStep[] = [
        {
          id: 'approve-delegate',
          label: 'Approve PositionRouter as delegate',
          shouldSkip: !!isDelegated,
          status: 'idle',
        },
        {
          id: 'router-withdraw',
          label: `Withdraw ${symbol}`,
          shouldSkip: false,
          status: 'idle',
        },
      ];
      setItems(plan);

      await runStep({
        ...getRouterQueueStep(plan, 'approve-delegate'),
        action: () =>
          walletClient.writeContract({
            address: poolComptrollerAddress as Address,
            abi: isolatedPoolComptrollerAbi,
            functionName: 'updateDelegate',
            args: [routerAddress as Address, true],
            account: accountAddress,
          }),
      });

      let shares: bigint;
      let expectedWrapperOut: bigint;

      if (withdrawFullSupply) {
        // type(uint256).max sentinel: the router resolves max-shares on-chain
        // via vToken.balanceOf, so any block-by-block accrual is consumed
        // atomically without leaving dust.
        shares = BigInt(MAX_UINT256.toFixed());
        const [exchangeRate, onChainVTokenBalance] = await Promise.all([
          getExchangeRateStored({ publicClient, vTokenAddress: vToken.address }),
          publicClient.readContract({
            address: vToken.address,
            abi: vBep20Abi,
            functionName: 'balanceOf',
            args: [accountAddress],
          }),
        ]);
        if (exchangeRate <= 0n) {
          throw new Error('Invalid vToken exchange rate');
        }
        expectedWrapperOut = (onChainVTokenBalance * exchangeRate) / ONE_TOKEN;
      } else {
        const exchangeRate = await getExchangeRateStored({
          publicClient,
          vTokenAddress: vToken.address,
        });
        if (exchangeRate <= 0n) {
          throw new Error('Invalid vToken exchange rate');
        }
        shares = (underlyingAmountMantissa * ONE_TOKEN + (exchangeRate - 1n)) / exchangeRate;
        expectedWrapperOut = underlyingAmountMantissa;
      }

      // Each redeem* entrypoint expects minOut in a different decimal scale —
      // compute three distinct floors and dispatch the matching one.
      //   - redeem      → wrapper ERC-20 base units (18-dec), no rescale
      //   - redeemTAO   → native TAO wei (18-dec), rescale by wsTAO rate
      //   - redeemAlpha → native alpha RAO (9-dec), rescale by wAlpha rate
      const minWrapperOut = calculateSlippageFloor({
        amount: expectedWrapperOut,
        slippagePercentage,
      });

      const minTaoOut =
        receiveNative && isWsTAOMarket
          ? await (async () => {
              const wsTaoRate = await publicClient.readContract({
                address: vToken.underlyingToken.address,
                abi: wsTAOAbi,
                functionName: 'exchangeRate',
              });
              return computeNativeOutFloor({
                wrapperAmount: expectedWrapperOut,
                exchangeRate: wsTaoRate,
                slippagePercentage,
              });
            })()
          : 0n;

      const minAlphaOut =
        receiveNative && !isWsTAOMarket
          ? await (async () => {
              const wAlphaRate = await publicClient.readContract({
                address: vToken.underlyingToken.address,
                abi: wAlphaAbi,
                functionName: 'exchangeRate',
              });
              return computeNativeOutFloor({
                wrapperAmount: expectedWrapperOut,
                exchangeRate: wAlphaRate,
                slippagePercentage,
              });
            })()
          : 0n;

      const deadline = computeRouterDeadline();

      await runStep({
        ...getRouterQueueStep(plan, 'router-withdraw'),
        action: () => {
          if (!receiveNative) {
            return walletClient.writeContract({
              address: routerAddress as Address,
              abi: positionRouterAbi,
              functionName: 'redeem',
              args: [vToken.address, shares, minWrapperOut, deadline],
              account: accountAddress,
            });
          }
          if (isWsTAOMarket) {
            return walletClient.writeContract({
              address: routerAddress as Address,
              abi: positionRouterAbi,
              functionName: 'redeemTAO',
              args: [vToken.address, shares, minTaoOut, deadline],
              account: accountAddress,
            });
          }
          return walletClient.writeContract({
            address: routerAddress as Address,
            abi: positionRouterAbi,
            functionName: 'redeemAlpha',
            args: [vToken.address, shares, minAlphaOut, deadline],
            account: accountAddress,
          });
        },
      });

      invalidateRouterQueries({
        accountAddress,
        chainId,
        poolComptrollerAddress: poolComptrollerAddress as Address,
        routerAddress: routerAddress as Address,
        vTokenAddresses: [vToken.address],
        tokenAddress: vToken.underlyingToken.address,
      });
    } catch (error) {
      setItems(failedStepStateReducer);
      throw wrapPositionRouterError(error);
    } finally {
      setIsRunning(false);
    }
  };

  return { execute, isRunning, items };
};
